import { useApolloClient } from "@apollo/client";
import { useState } from "react";
import { StyleProp, ViewStyle } from "react-native";
import AddPhotosLabelDocument from "src/api/graphql/__generated__/documents/AddPhotosLabelDocument";
import RemovePhotosLabelDocument from "src/api/graphql/__generated__/documents/RemovePhotosLabelDocument";
import LabelVisibility from "src/api/graphql/__generated__/enums/LabelVisibility";
import { SelectedPhoto, useGatheringStore } from "src/contexts/GatheringContext";
import useStyle from "src/hooks/useStyle";
import IconAnnotationQuestion from "src/icons/IconAnnotationQuestion";
import { color } from "src/styles";
import { alert, prompt } from "src/swsh-native";
import FloatingActionItem from "./FloatingActionItem";

const FloatingLabelButton = ({
	containerStyle,
	selectedPhotos,
}: {
	containerStyle?: StyleProp<ViewStyle>;
	selectedPhotos: SelectedPhoto[];
}) => {
	const { theme } = useStyle();
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const resetSelectMode = useGatheringStore((state) => state.resetSelectMode);

	const [loading, setLoading] = useState(false);
	const apolloClient = useApolloClient();

	return (
		<FloatingActionItem
			Icon={IconAnnotationQuestion}
			loading={loading}
			containerStyle={containerStyle}
			color={color.Text[theme]}
			testID="Select-Label"
			onPress={async () => {
				const handleRemoveLabel = async ({
					label,
					visibility,
				}: {
					label: string | undefined;
					visibility: LabelVisibility;
				}) => {
					if (!label) return;
					setLoading(true);
					await apolloClient
						.mutate({
							mutation: RemovePhotosLabelDocument,
							variables: {
								input: {
									label,
									photoIds: selectedPhotos.map((photo) => photo.photoId),
									gatheringId,
									visibility,
								},
							},
						})
						.then(() => {
							resetSelectMode();
						})
						.finally(() => {
							setLoading(false);
						});
				};
				const handleApplyLabel = async ({
					label,
					visibility,
				}: {
					label: string | undefined;
					visibility: LabelVisibility;
				}) => {
					if (!label) return;
					setLoading(true);
					await apolloClient
						.mutate({
							mutation: AddPhotosLabelDocument,
							variables: {
								input: {
									label,
									photoIds: selectedPhotos.map((photo) => photo.photoId),
									gatheringId,
									visibility,
								},
							},
						})
						.then(() => {
							resetSelectMode();
						})
						.finally(() => {
							setLoading(false);
						});
				};
				alert({
					title: "Label Photos",
					buttons: [
						{
							style: "cancel",
							text: "Cancel",
						},
						{
							text: "Remove Label",
							style: "destructive",
							onPress: () => {
								alert({
									title: "Label Settings",
									buttons: [
										{
											style: "cancel",
											text: "Cancel",
										},
										{
											text: "Hidden Label",
											onPress: () => {
												prompt({
													title: "Remove Hidden Label",
													buttons: [
														{
															style: "cancel",
															text: "Cancel",
														},
														{
															text: "Remove",
															style: "destructive",
															onPress: (text) => {
																handleRemoveLabel({
																	label: text,
																	visibility:
																		LabelVisibility.Admin,
																});
															},
														},
													],
												});
											},
										},
										{
											text: "Public Label",
											onPress: () => {
												prompt({
													title: "Remove Public Label",
													buttons: [
														{
															style: "cancel",
															text: "Cancel",
														},
														{
															text: "Remove",
															style: "destructive",
															onPress: (text) => {
																handleRemoveLabel({
																	label: text,
																	visibility:
																		LabelVisibility.Public,
																});
															},
														},
													],
												});
											},
										},
									],
								});
							},
						},
						{
							text: "Apply Label",
							style: "default",
							onPress: () => {
								alert({
									title: "Label Settings",
									buttons: [
										{
											style: "cancel",
											text: "Cancel",
										},
										{
											text: "Hidden Label",
											onPress: () => {
												prompt({
													title: "Apply Hidden Label",
													buttons: [
														{
															style: "cancel",
															text: "Cancel",
														},
														{
															text: "Apply",
															onPress: (text) => {
																handleApplyLabel({
																	label: text,
																	visibility:
																		LabelVisibility.Admin,
																});
															},
														},
													],
												});
											},
										},
										{
											text: "Public Label",
											onPress: () => {
												prompt({
													title: "Apply Public Label",
													buttons: [
														{
															style: "cancel",
															text: "Cancel",
														},
														{
															text: "Apply",
															onPress: (text) => {
																handleApplyLabel({
																	label: text,
																	visibility:
																		LabelVisibility.Public,
																});
															},
														},
													],
												});
											},
										},
									],
								});
							},
						},
					],
				});
			}}
		/>
	);
};
export default FloatingLabelButton;
